import { Linkified } from "../../components/Linkified";
import { AnimatePresence, motion } from "motion/react";
import { Check, ChevronDown, Flag, Pencil, RotateCcw, Send } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Dropzone, UploadRow } from "../../components/FileDrop";
import { VideoRemove } from "../../components/VideoRemove";
import { FrameDrawing, hasDrawing } from "../../components/FrameDrawing";
import { Button } from "../../components/ui/button";
import { FileList, ModalSection } from "../../components/ModalParts";
import { Chip } from "../../components/ui/primitives";
import { ME_EDITOR, PEOPLE, projectPublishesAfterModeration, taskModerated } from "../../data/people";
import { useAccountName } from "../../lib/accounts";
import { authorOf, CAPACITY, dayLoad, dayTaskCount, defaultDue, dueOptions, isPastDay, isPostItem, liveItems, revisionAt, tasksLabel } from "../../lib/domain";
import { spring } from "../../lib/motion";
import { notify } from "../../lib/notify";
import { at, dayInfo, dayName, dayOf, dayOn, DAYS, nowMin, hourOf, timeOf } from "../../lib/time";
import { useNow } from "../../lib/useNow";
import { cn, formatDuration, plural } from "../../lib/utils";
import { type DraftInput, useStore } from "../../store";
import { confirmDialog } from "../../../../shared/ui/ConfirmHost";
import type { UploadItem } from "../../components/FileDrop";
import { addFiles as bgAddFiles, clear as bgClear, removeFile as bgRemoveFile, setComment as bgSetComment, submitWhenReady as bgSubmitWhenReady, useBgUploads } from "../../lib/backgroundUploads";
import type { FrameDrawing as FrameDrawingValue, Revision, SubTask, Task } from "../../types";

const clone = (t: Task): Task => JSON.parse(JSON.stringify(t));

export function useEditorActions() {
  const accept = useStore((s) => s.accept);
  const propose = useStore((s) => s.propose);
  const submitDraft = useStore((s) => s.submitDraft);
  const restore = useStore((s) => s.restore);
  return {
    accept(task: Task) {
      const snap = clone(task);
      accept(task.id);
      notify(`В работе — ${PEOPLE[task.creator].name} видит это на доске`, { icon: "check", undo: () => restore(snap) });
    },
    propose(task: Task, day: number, due: number, note: string) {
      const snap = clone(task);
      propose(task.id, day, due, note);
      notify(`Предложение ушло · ${PEOPLE[task.creator].name}: ${dayOn(day, nowMin())}`, { icon: "send", undo: () => restore(snap) });
    },
    submit(task: Task, item: SubTask, d: DraftInput) {
      const snap = clone(task);
      submitDraft(task.id, item.id, d);
      notify(`«${item.title}» v${item.versions.length + 1} ушла модератору`, { icon: "send", undo: () => restore(snap) });
    },
  };
}

/**
 * Состояние загрузки черновика ОДНОГО ролика: файл сразу уходит в каталог
 * задачи, рядом — кадр-превью. Свой хук на ролик, а не один на задачу: в дне
 * их десять, и грузятся они независимо.
 */
/** Пост (картинки, карусель) или ролик — по формату. */
export const isPost = (item: { format: string }) => /пост|карусел/i.test(item.format);

export function useDraft(task: Task, item: SubTask) {
  // Загрузка — в фоне, по id ролика (09.10.2026): переживает закрытие окна и
  // смену id новой публикации. См. lib/backgroundUploads.
  const entry = useBgUploads((s) => s.entries[item.id]);
  const files = entry?.files ?? NO_FILES;
  const uploads = {
    items: files,
    addFiles: (f: File[]) => bgAddFiles(item.id, task.id, item.title, f),
    remove: (fileId: string) => bgRemoveFile(item.id, fileId),
  };
  const post = isPost(item);
  const file = post ? files[0] : files[files.length - 1];
  const comment = entry?.comment ?? "";
  const setComment = (v: string) => bgSetComment(item.id, task.id, item.title, v);
  const n = item.versions.length + 1;
  const guessDuration = () => {
    const m = item.format.match(/(\d+)\s*с/);
    return m ? Math.max(12, Number(m[1]) - 2) : 38;
  };
  const payloadOf = (list: UploadItem[], text: string): DraftInput => {
    const f = post ? list[0] : list[list.length - 1];
    return post ? ({
      path: f?.path ?? "",
      fileName: f?.name ?? "1.jpg",
      size: list.reduce((s, x) => s + x.size, 0),
      duration: 0,
      comment: text,
      images: list.map((x) => ({ path: x.path!, name: x.name, size: x.size, url: x.url })),
    }) : ({
      path: f?.path ?? "",
      posterPath: f?.posterPath,
      fileName: f?.name ?? `v${n}.mp4`,
      size: f?.size ?? 0,
      duration: f?.duration ?? guessDuration(),
      videoUrl: f?.url,
      comment: text,
    });
  };
  return {
    uploads,
    file,
    comment,
    setComment,
    n,
    post,
    /** файл выбран — сдать можно, даже если он ещё грузится */
    chosen: files.length > 0,
    uploading: files.some((f) => !f.done),
    ready: post
      ? files.length > 0 && files.every((f) => f.done && !!f.path)
      : !!file && file.done && !!file.path,
    payload: () => payloadOf(files, comment),
    payloadOf,
    waiting: Boolean(entry?.submit),
  };
}

const NO_FILES: UploadItem[] = [];

export type Draft = ReturnType<typeof useDraft>;

export function DraftArea({ item, draft, compact }: { item: SubTask; draft: Draft; compact?: boolean }) {
  const { file } = draft;
  return (
    <div className={cn("flex flex-col", compact ? "gap-2.5" : "gap-4")}>
      {compact ? (
        <h3 className="flex h-6 items-center gap-1.5 text-[13px] font-semibold">
          Черновик на проверку
          <span className="font-medium text-muted-foreground">v{draft.n}</span>
        </h3>
      ) : (
        <div className="flex items-center gap-2.5">
          <span className="text-lg font-semibold tracking-[-0.01em]">Черновик на проверку</span>
          <Chip>v{draft.n}</Chip>
        </div>
      )}
      {draft.post ? <PostImages draft={draft} compact={compact} /> : (
      <AnimatePresence mode="popLayout" initial={false}>
        {!file ? (
          <motion.div key="drop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, scale: 0.98 }}>
            <Dropzone
              onFiles={(f) => draft.uploads.addFiles(f.slice(0, 1))}
              multiple={false}
              accept="video/*"
              title="Перетащите черновик сюда или"
              hint="MP4 или MOV"
              className={compact ? "min-h-[120px]" : "min-h-[180px]"}
            />
          </motion.div>
        ) : (
          <motion.div key="file" layout initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={spring.smooth} className="flex gap-5">
            <div className={cn("relative shrink-0 overflow-hidden rounded-[20px] bg-player", compact ? "h-[240px] w-[135px]" : "h-[400px] w-[225px]")}>
              {file.url ? (
                <video src={file.url} muted autoPlay loop playsInline className="size-full object-cover" />
              ) : (
                <img src={item.poster} alt="" className="size-full object-cover" draggable={false} />
              )}
              {!file.done && (
                <div className="absolute inset-0 grid place-items-center bg-black/45 text-sm font-semibold text-white tabular-nums">{Math.round(file.progress * 100)}%</div>
              )}
              <span className="absolute bottom-2.5 left-2.5 rounded-full bg-black/65 px-2 py-0.5 font-mono text-[11px] font-semibold text-white">
                {formatDuration(file.duration ?? 0)}
              </span>
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-3">
              <UploadRow item={file} onRemove={() => draft.uploads.remove(file.id)} />
              <Dropzone
                compact
                onFiles={(f) => {
                  draft.uploads.remove(file.id);
                  draft.uploads.addFiles(f.slice(0, 1));
                }}
                multiple={false}
                accept="video/*"
                title="Заменить файл —"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      )}
      <div className="flex flex-col gap-2">
        <span className={compact ? "text-[13px] font-semibold" : "text-sm font-medium"}>Комментарий для контент-мейкера</span>
        <textarea
          value={draft.comment}
          onChange={(e) => draft.setComment(e.target.value)}
          rows={compact ? 2 : 3}
          placeholder="Что учли, что стоит проверить…"
          className="w-full resize-none rounded-[20px] border border-input bg-background px-[18px] py-3 text-sm leading-relaxed shadow-mxs outline-none placeholder:text-muted-foreground focus:ring-4 focus:ring-ring/20"
        />
      </div>
    </div>
  );
}

/**
 * Пост: 1–10 картинок по порядку — так же, как карусель в Instagram (08.10.2026).
 * Первая — обложка на доске.
 */
function PostImages({ draft, compact }: { draft: Draft; compact?: boolean }) {
  const files = draft.uploads.items;
  const left = 10 - files.length;
  return (
    <div className="flex flex-col gap-3">
      {files.length > 0 && (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
          {files.map((f, i) => (
            <div key={f.id} className="relative aspect-[4/5] overflow-hidden rounded-[14px] bg-muted">
              {f.url && <img src={f.url} alt="" className="size-full object-cover" draggable={false} />}
              <span className="absolute left-1.5 top-1.5 grid size-5 place-items-center rounded-full bg-black/65 text-[11px] font-semibold text-white">{i + 1}</span>
              {!f.done && <div className="absolute inset-0 grid place-items-center bg-black/45 text-xs font-semibold text-white tabular-nums">{Math.round(f.progress * 100)}%</div>}
              <button
                type="button"
                onClick={() => draft.uploads.remove(f.id)}
                title="Убрать картинку"
                className="absolute right-1.5 top-1.5 grid size-6 place-items-center rounded-full bg-black/65 text-white"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
      {left > 0 && (
        <Dropzone
          compact={files.length > 0}
          onFiles={(f) => draft.uploads.addFiles(f.filter((x) => x.type.startsWith("image/") || /\.(jpe?g|png|webp|heic)$/i.test(x.name)).slice(0, left))}
          multiple
          accept="image/*"
          title={files.length ? "Добавить ещё —" : "Перетащите картинки поста сюда или"}
          hint={`JPG или PNG, до 10 штук — порядок как в карусели`}
          className={files.length ? undefined : compact ? "min-h-[120px]" : "min-h-[180px]"}
        />
      )}
    </div>
  );
}

export function SubmitDraftButton({ task, item, draft, onDone, size }: { task: Task; item: SubTask; draft: Draft; onDone?: () => void; size?: "default" | "lg" }) {
  const actions = useEditorActions();
  const submitDraft = useStore((s) => s.submitDraft);
  return (
    <Button
      size={size ?? "default"}
      disabled={!draft.chosen || draft.waiting}
      title={!draft.chosen ? (draft.post ? "Сначала загрузите картинки" : "Сначала загрузите черновик") : draft.uploading ? "Сдастся сам, когда файл догрузится — окно можно закрыть" : undefined}
      onClick={() => {
        if (draft.ready) {
          actions.submit(task, item, draft.payload());
          bgClear(item.id);
        } else {
          // Файл ещё грузится: сдастся сам по окончании, окно можно закрыть.
          const comment = draft.comment;
          const title = item.title;
          const n = item.versions.length + 1;
          bgSubmitWhenReady(item.id, (taskId, itemId, files) => {
            submitDraft(taskId, itemId, draft.payloadOf(files, comment));
            notify(`«${title}» v${n} догрузилась и ушла на проверку`, { icon: "send" });
          });
          notify(`«${item.title}» сдастся, как только файл догрузится`, { icon: "send" });
        }
        onDone?.();
      }}
    >
      <Send />
      {draft.waiting ? "Сдастся после загрузки" : draft.uploading ? "Сдать после загрузки" : draft.post ? "Сдать пост" : "Сдать ролик"}
    </Button>
  );
}

/**
 * Один ролик, который монтажёру сейчас сдавать: правки к прошлой версии,
 * загрузка черновика и кнопка. Своя карточка на ролик — потому что и сдаются
 * они по одному.
 */
export function ItemDraftCard({ task, item }: { task: Task; item: SubTask }) {
  const draft = useDraft(task, item);
  const n = task.items.indexOf(item) + 1;
  return (
    <div className="flex flex-col gap-3 rounded-[20px] border border-input bg-background/60 p-4">
      <div className="flex items-center gap-2.5">
        <span className="grid size-5 shrink-0 place-items-center rounded-full bg-foreground text-[11px] font-semibold text-background">{n}</span>
        <span className="min-w-0 flex-1 truncate text-sm font-semibold">{item.title}</span>
        <span className="shrink-0 text-xs text-muted-foreground">{item.format}</span>
        {/* Публикация — один ролик: удалять его из неё незачем (08.10.2026). */}
        {liveItems(task).length > 1 && <VideoRemove task={task} item={item} />}
      </div>
      {item.status === "rework" && <ReworkList task={task} item={item} />}
      {item.brief && <p className="whitespace-pre-wrap text-[13px] leading-relaxed text-muted-foreground [overflow-wrap:anywhere]"><Linkified text={item.brief} /></p>}
      {item.files.length > 0 && <FileList files={item.files} download />}
      <DraftArea item={item} draft={draft} compact />
      <div className="flex flex-wrap items-center justify-end gap-3">
        <PublishAfterModeration task={task} item={item} />
        <SubmitDraftButton task={task} item={item} draft={draft} />
      </div>
    </div>
  );
}

/**
 * «Опубликовать после модерации» (23.10.2026, «Президент»): после «Норм»
 * ролик сам уходит в Instagram, ссылка встаёт сама — без аппрува и без
 * ручной ссылки. Только для Instagram-ролика, который смотрит модератор.
 * С 28.10.2026 не выключается — монтажёр видит пометку вместо галочки.
 */
export function PublishAfterModeration({ task, item, className }: { task: Task; item: SubTask; className?: string }) {
  const account = useAccountName(item.accountId);
  const instagram = Boolean(item.accountId) && !item.accountId!.startsWith("tt:") && !item.accountId!.startsWith("yt:");
  if (task.editor !== ME_EDITOR || !instagram || !projectPublishesAfterModeration(task.projectId) || !taskModerated(task, item)) return null;
  if (!["assigned", "uploaded", "rework", "moderated"].includes(item.status)) return null;
  return (
    <p className={cn("text-[13px] text-muted-foreground", className)}>
      После модерации опубликуется в {account ?? "Instagram"} автоматически
    </p>
  );
}

/**
 * Монтажёр забирает сданную версию с проверки: «не тот файл», «забыл
 * субтитры». Пока ролик никто не посмотрел — потом сервер откажет. Версия
 * удаляется, ролик снова «сдать» (23.10.2026: раньше это была мелкая ссылка
 * только внутри окна, и монтажёры её не находили).
 */
export function WithdrawSubmission({ task, item, size = "sm", className }: { task: Task; item: SubTask; size?: "sm" | "default"; className?: string }) {
  const withdraw = useStore((s) => s.withdraw);
  const [busy, setBusy] = useState(false);
  if (task.editor !== ME_EDITOR || item.status !== "uploaded") return null;
  const n = item.versions.length;
  const where = taskModerated(task, item) ? "с модерации" : "с проверки";
  return (
    <Button
      type="button"
      size={size}
      variant="outline"
      disabled={busy}
      className={className}
      title="Файл удалится, ролик можно будет сдать заново"
      onClick={async (e) => {
        e.stopPropagation();
        if (!(await confirmDialog({ title: `Отозвать v${n} «${item.title}» ${where}?`, description: "Файл удалится, ролик можно будет сдать заново.", confirmText: "Отозвать" }))) return;
        setBusy(true);
        try {
          await withdraw(task.id, item.id);
          // Отказ сервера стор уже показал тостом — «отозвана» только если правда.
          const after = useStore.getState().tasks.find((t) => t.id === task.id)?.items.find((i) => i.id === item.id);
          if (after && after.status !== "uploaded") notify(`«${item.title}» — v${n} отозвана, можно сдать заново`, { icon: "info" });
        } finally {
          setBusy(false);
        }
      }}
    >
      <RotateCcw />
      {busy ? "Отзываем…" : `Отозвать ${where}`}
    </Button>
  );
}

export function ReworkList({ task, item }: { task: Task; item: SubTask }) {
  const toggle = useStore((s) => s.toggleRevision);
  const list = item.revisions.filter((r) => r.round === item.round - 1);
  const done = list.filter((r) => r.resolved).length;
  if (list.length === 0) return null;
  return (
    <ModalSection title={`Правки к v${item.round - 1}`} right={<span className="text-xs font-medium text-muted-foreground">учтено {done} из {list.length}</span>}>
      <div className="flex flex-col gap-1.5">
        {list.map((r) => (
          <div key={r.id} className="overflow-hidden rounded-xl bg-muted/60">
            <button type="button" onClick={() => toggle(task.id, item.id, r.id)} aria-pressed={r.resolved} className="flex w-full items-start gap-2.5 p-2.5 text-left">
              <span className="inline-flex h-[22px] shrink-0 items-center rounded-full bg-foreground px-2 font-mono text-[11px] font-semibold text-background">{revisionAt(r.tc, isPostItem(item))}</span>
              <span className={cn("min-w-0 flex-1 whitespace-pre-wrap text-[13px] leading-relaxed [overflow-wrap:anywhere]", r.resolved && "text-muted-foreground line-through decoration-muted-foreground/40")}>
                {authorOf(task, item, r) && <span className="text-muted-foreground">{authorOf(task, item, r)}: </span>}
                {r.text}
              </span>
              <motion.span
                animate={{ backgroundColor: r.resolved ? "#22c55e" : "#ffffff", borderColor: r.resolved ? "#22c55e" : "#e5e5e5" }}
                transition={{ duration: 0.15 }}
                className="grid size-[18px] shrink-0 place-items-center rounded-full border-[1.5px]"
              >
                {r.resolved && <Check className="size-3 text-white" strokeWidth={3} />}
              </motion.span>
            </button>
            {hasDrawing(r.drawing) && <RevisionFramePreview item={item} revision={r} drawing={r.drawing!} />}
          </div>
        ))}
      </div>
    </ModalSection>
  );
}

function RevisionFramePreview({ item, revision, drawing }: { item: SubTask; revision: Revision; drawing: FrameDrawingValue }) {
  const video = useRef<HTMLVideoElement>(null);
  const [expanded, setExpanded] = useState(false);
  const version = item.versions.find((candidate) => candidate.n === revision.round);
  const seek = () => {
    if (video.current && Number.isFinite(video.current.duration)) {
      video.current.currentTime = Math.min(revision.tc, Math.max(0, video.current.duration - 0.05));
    }
  };
  useEffect(seek, [revision.tc, version?.videoUrl]);
  return (
    <div className="flex items-start gap-3 border-t border-border/60 px-2.5 pb-2.5 pt-2">
      <button
        type="button"
        onClick={() => setExpanded((value) => !value)}
        className={cn(
          "relative aspect-[9/16] shrink-0 overflow-hidden rounded-[12px] bg-player outline-none transition-[width] focus-visible:ring-2 focus-visible:ring-rework/50",
          expanded ? "w-[220px] max-w-[70vw]" : "w-[120px]",
        )}
        title={expanded ? "Уменьшить кадр" : "Увеличить кадр"}
      >
        {version?.videoUrl ? (
          <video ref={video} src={version.videoUrl} muted playsInline preload="metadata" onLoadedMetadata={seek} className="size-full object-cover" />
        ) : (
          <img src={item.poster} alt="Кадр с пометкой" className="size-full object-cover" draggable={false} />
        )}
        <FrameDrawing value={drawing} />
        <span className="absolute bottom-1.5 left-1.5 z-30 rounded-full bg-black/75 px-2 py-1 font-mono text-[10px] font-semibold text-white">
          {revisionAt(revision.tc, false)}
        </span>
      </button>
      <span className="mt-1 flex flex-col gap-1 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5 font-medium text-rework-fg">
          <Pencil className="size-3.5" />
          Пометка модератора на кадре v{revision.round}
        </span>
        <span>Нажмите на кадр, чтобы {expanded ? "уменьшить" : "увеличить"}</span>
      </span>
    </div>
  );
}

/** «Не успеваю» — предложить постановщику другой день (задачи делаются день в день) */
export function ProposeDay({ task, onDone, onCancel }: { task: Task; onDone: () => void; onCancel: () => void }) {
  const now = useNow();
  const tasks = useStore((s) => s.tasks);
  const busy = useStore((s) => s.busy);
  const actions = useEditorActions();
  const creator = PEOPLE[task.creator];
  const others = tasks.filter((t) => t.id !== task.id);
  const [day, setDay] = useState(() => {
    // Ближайший день после задачи, где у монтажёра есть место. Дни — пять
    // рабочих начиная с сегодняшнего, поэтому в пятницу это уже понедельник.
    const free = DAYS.find((d) => d.idx > task.day && d.idx >= dayOf(now) && dayLoad(task.editor, d.idx, others, busy) + task.estimate <= CAPACITY);
    return (free ?? DAYS.find((d) => d.idx > task.day) ?? DAYS[DAYS.length - 1]).idx;
  });
  const [due, setDue] = useState(() => at(day, hourOf(task.due)));
  const [note, setNote] = useState(`${dayName(task.day, now)} не успеваю — могу ${dayInfo(day).on}`);

  const pickDay = (d: number) => {
    setDay(d);
    const same = at(d, hourOf(due));
    setDue(dueOptions(d, now).some((o) => o.value === same) ? same : defaultDue(d, now));
    setNote(`${dayName(task.day, now)} не успеваю — могу ${dayOn(d, now)}`);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <span className="text-[13px] font-semibold">Предложить другой день</span>
      </div>
      <div className="grid grid-cols-5 gap-2">
        {DAYS.map((d) => {
          const past = isPastDay(d.idx, now);
          const current = d.idx === task.day;
          const count = dayTaskCount(task.editor, d.idx, others, busy) + (d.idx === day ? liveItems(task).length : 0);
          const active = d.idx === day;
          return (
            <motion.button
              key={d.idx}
              type="button"
              disabled={past || current}
              onClick={() => pickDay(d.idx)}
              whileTap={{ scale: 0.97 }}
              transition={spring.snappy}
              className={cn(
                "flex flex-col gap-2 rounded-[20px] border p-3 text-left transition-colors",
                active ? "border-primary bg-background shadow-mmd ring-1 ring-primary" : "bg-muted/50 hover:bg-muted",
                (past || current) && "cursor-not-allowed opacity-50 hover:bg-muted/50",
              )}
            >
              <span className="flex items-center justify-between">
                <span className="text-sm font-semibold">
                  {d.abbr} {d.date}
                </span>
                {current && <span className="text-[11px] font-medium text-muted-foreground">сейчас</span>}
                {d.idx === dayOf(now) && !current && <span className="text-[11px] font-medium text-work-fg">сегодня</span>}
              </span>
              <span className="text-xs text-muted-foreground">{tasksLabel(count)}</span>
            </motion.button>
          );
        })}
      </div>
      <div className="flex items-center gap-2.5">
        <label className="relative inline-flex h-10 shrink-0 items-center gap-1.5 rounded-full border bg-background pl-3.5 pr-3 text-[13px] font-medium hover:bg-muted">
          <Flag className="size-3.5 text-muted-foreground" />
          {dueOptions(day, now).find((o) => o.value === due)?.label ?? `до ${timeOf(due)}`}
          <ChevronDown className="size-3.5 text-muted-foreground" />
          <select value={due} onChange={(e) => setDue(Number(e.target.value))} className="absolute inset-0 cursor-pointer opacity-0">
            {dueOptions(day, now).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Комментарий для контент-мейкера"
          className="h-10 min-w-0 flex-1 rounded-full border border-input bg-background px-4 text-sm shadow-mxs outline-none focus:ring-4 focus:ring-ring/20"
        />
        <Button variant="outline" onClick={onCancel}>
          Отмена
        </Button>
        <Button
          onClick={() => {
            actions.propose(task, day, due, note);
            onDone();
          }}
        >
          <Send />
          Отправить на проверку
        </Button>
      </div>
    </div>
  );
}

export const newTasksLabel = (n: number) => `${n} ${plural(n, ["новая задача ждёт", "новые задачи ждут", "новых задач ждут"])} ответа`;
